'use client';

import { useEffect } from 'react';

import { GripVertical, X } from 'lucide-react';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import type { MarketSelectField as MarketSelectFieldDef } from '../fields';
import { useEvent, type WidgetMarket } from '../hooks/useEvent';
import { useMarketDragDrop } from '../hooks/useMarketDragDrop';

const marketLabel = (market: WidgetMarket): string => {
    const name = market.yes_sub_title || market.title;
    return `${name} (${market.last_price}¢)`;
};

interface MarketSelectFieldProps {
    field: MarketSelectFieldDef;
    value: string[];
    onChange: (ids: string[]) => void;
    eventTicker: string;
}

export function MarketSelectField({ field, value, onChange, eventTicker }: MarketSelectFieldProps) {
    const { data: event, isLoading, isError } = useEvent(eventTicker);
    const markets = event?.markets ?? [];
    const { mode, min = 1, max = mode === 'single' ? 1 : Infinity, autoSelectTopN } = field;

    // Market IDs are event-specific. When the event's markets resolve -- especially after
    // the user switches events -- drop any selected IDs that aren't in this event, so a prior
    // event's selection (e.g. the default 2028-nominee markets) can't persist and render no
    // candidates. Then auto-select the top-N by price when nothing is selected.
    useEffect(() => {
        if (markets.length === 0) return;
        const valid = value.filter((id) => markets.some((m) => m.id === id));
        if (valid.length !== value.length) {
            onChange(valid);
            return;
        }
        if (autoSelectTopN && value.length === 0) {
            const top = [...markets]
                .sort((a, b) => b.last_price - a.last_price)
                .slice(0, autoSelectTopN)
                .map((m) => m.id);
            onChange(top);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoSelectTopN, markets, value]);

    const dnd = useMarketDragDrop({ selected: value, setSelected: onChange, min, max });

    if (isLoading) return <p className="typ-body-x10 text-text-x20">Loading markets…</p>;
    if (isError || markets.length === 0)
        return <p className="typ-body-x10 text-text-x20">No markets available for this event.</p>;

    if (mode === 'single') {
        return (
            <Select value={value[0] ?? ''} onValueChange={(id) => onChange([id])}>
                <SelectTrigger>
                    <SelectValue placeholder="Select a market" />
                </SelectTrigger>
                <SelectContent>
                    {markets.map((market) => (
                        <SelectItem key={market.id} value={market.id}>
                            {marketLabel(market)}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        );
    }

    const selectedMarkets = value.map((id) => markets.find((m) => m.id === id)).filter(Boolean) as WidgetMarket[];
    const available = markets.filter((m) => !value.includes(m.id));

    return (
        <div className="space-y-2">
            <ul className="space-y-1">
                {selectedMarkets.map((market, index) => (
                    <li
                        key={market.id}
                        {...dnd.dragHandlers(index)}
                        className="typ-body-x30 flex items-center gap-2 rounded-x30 border border-stroke-x30 bg-surface-x10 px-2 py-1.5 text-text-x10"
                    >
                        <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-text-x30" />
                        <span className="truncate">{marketLabel(market)}</span>
                        <button
                            type="button"
                            onClick={() => dnd.remove(market.id)}
                            disabled={!dnd.canRemove}
                            aria-label={`Remove ${market.title}`}
                            className="ml-auto shrink-0 rounded-x20 p-0.5 text-text-x30 transition-colors hover:text-text-x10 disabled:opacity-30"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </li>
                ))}
            </ul>

            {dnd.canAdd && available.length > 0 && (
                <Select value="" onValueChange={(id) => dnd.add(id)}>
                    <SelectTrigger>
                        <SelectValue placeholder={`Add a market${Number.isFinite(max) ? ` (up to ${max})` : ''}`} />
                    </SelectTrigger>
                    <SelectContent>
                        {available.map((market) => (
                            <SelectItem key={market.id} value={market.id}>
                                {marketLabel(market)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            )}
        </div>
    );
}
